import { z } from 'zod'
import { IngestionPolicySchema, SearchRequestSchema } from './api.ts'
import { SourceFoldersSummarySchema } from './connectors.ts'
import { ACK_TEXT_MAX, ackRecordFields } from './consent.ts'
import { GroupRefSchema, GroupWithSizeSchema, OwnerRefSchema, OwnerViewSchema } from './groups.ts'
import { AudienceInputSchema, AudienceViewSchema } from './sharing.ts'

/**
 * Knowledge bases (PRD-26 D-9, D-10): one kind, owned by a person or a group, used by whoever it
 * is shared with, maintained by its owner's side.
 *
 * "Base" is the word everywhere, for maintainers' sources and people's own uploads alike — they were
 * two surfaces over one table with two permission models (F-6, F-23), and now they are one. None
 * of the ingestion vocabulary (chunks, embeddings, indexes) appears in these shapes.
 */

/** Why this reader can use a base, most direct first, and whether they maintain it. */
export const KnowledgeBaseViewerSchema = z.object({
  canUse: z.boolean(),
  canMaintain: z.boolean(),
  /**
   * - `maintainer` — they own it, or manage the group that does;
   * - `group` — it is shared with a group they are in (`viaGroups` names which);
   * - `everyone` — it is shared with everyone;
   * - `administrator` — they can open it only because they are an administrator (recorded, D-18);
   * - `none` — they cannot use it (an admin listing everything, or a base lent to an agent).
   */
  via: z.enum(['maintainer', 'group', 'everyone', 'administrator', 'none']),
  viaGroups: z.array(GroupRefSchema)
})
export type KnowledgeBaseViewer = z.infer<typeof KnowledgeBaseViewerSchema>

export const KnowledgeBaseStatusSchema = z.enum(['empty', 'reading', 'ready', 'needs_attention'])

export const KnowledgeBaseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  owner: OwnerViewSchema,
  /** Who can use it: only its maintainers, specific groups, or everyone. */
  access: AudienceViewSchema,
  viewer: KnowledgeBaseViewerSchema,
  documentCount: z.number().int().nonnegative(),
  /** Documents an agent can actually cite: read, and holding passages. */
  answeringCount: z.number().int().nonnegative(),
  /** Read by an older pipeline than the one deployed, and re-readable. */
  outdatedCount: z.number().int().nonnegative(),
  storageBytes: z.number().int().nonnegative(),
  status: KnowledgeBaseStatusSchema,
  stale: z.boolean(),
  lastIngestedAt: z.iso.datetime().nullable(),
  freshnessSlaHours: z.number().int().positive(),
  ingestionPolicy: IngestionPolicySchema,
  connectedFolders: SourceFoldersSummarySchema,
  /** What it may hold (D-41); `null` when its owner is unlimited (an administrator). */
  limits: z.object({ documents: z.number().int().positive(), storageBytes: z.number().int().positive() }).nullable(),
  /** Maintainers only: the sharing request waiting for review, if any. */
  pendingRequest: z
    .object({
      id: z.uuid(),
      requestedAt: z.iso.datetime(),
      requestedKind: z.enum(['groups', 'everyone']),
      groups: z.array(GroupRefSchema)
    })
    .nullable(),
  /** Maintainers only: agents that have it attached (empty for everyone else). */
  attachedAgents: z.array(z.object({ agentId: z.string(), displayName: z.string() })),
  /** Maintainers only: how many agents it answers through for their whole audience (D-16). */
  lentToCount: z.number().int().nonnegative(),
  /** Maintainers only: open requests to let an agent answer from it. */
  pendingLendingRequests: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
})
export type KnowledgeBase = z.infer<typeof KnowledgeBaseSchema>

/**
 * What the caller may still create, drawn as meters rather than discovered at the refusal
 * (AGM-101, PRD-26 D-41). `null` limits are unlimited (an administrator).
 */
export const KnowledgeAllowanceSchema = z.object({
  personal: z
    .object({
      bases: z.number().int().positive(),
      used: z.number().int().nonnegative(),
      documentsPerBase: z.number().int().positive(),
      storagePerBaseBytes: z.number().int().positive()
    })
    .nullable(),
  /** The same for each group the caller manages. */
  groups: z.array(
    z.object({
      group: GroupRefSchema,
      bases: z.number().int().positive(),
      used: z.number().int().nonnegative(),
      documentsPerBase: z.number().int().positive(),
      storagePerBaseBytes: z.number().int().positive()
    })
  ),
  uploadMaxBytes: z.number().int().positive()
})
export type KnowledgeAllowance = z.infer<typeof KnowledgeAllowanceSchema>

export const KnowledgeBaseViewSchema = z.enum(['maintain', 'use', 'all'])
export type KnowledgeBaseView = z.infer<typeof KnowledgeBaseViewSchema>

export const KnowledgeBaseListSchema = z.object({
  items: z.array(KnowledgeBaseSchema),
  allowance: KnowledgeAllowanceSchema
})
export type KnowledgeBaseList = z.infer<typeof KnowledgeBaseListSchema>

export const CreateKnowledgeBaseRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).default(''),
    /** `me`, or a group the caller manages (administrators: any group). */
    owner: z
      .discriminatedUnion('kind', [
        z.object({ kind: z.literal('me') }).strict(),
        z.object({ kind: z.literal('group'), groupId: z.uuid() }).strict()
      ])
      .default({ kind: 'me' }),
    freshnessSlaHours: z.number().int().positive().max(87_600).optional(),
    ingestionPolicy: IngestionPolicySchema.optional()
  })
  .strict()
export type CreateKnowledgeBaseRequest = z.infer<typeof CreateKnowledgeBaseRequestSchema>

/** What a maintainer may change in place. Access, lending and owner have their own routes. */
export const UpdateKnowledgeBaseRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(2000).optional(),
    freshnessSlaHours: z.number().int().positive().max(87_600).optional(),
    ingestionPolicy: IngestionPolicySchema.optional()
  })
  .strict()

export const DeleteKnowledgeBaseRequestSchema = z.object({ confirmName: z.string().max(120).optional() }).strict()

/**
 * Changing who can use a base. `ackText` is the sentence the maintainer was shown, stored with the
 * change or the request it becomes — what a person agreed to is evidence (AGM-042), and since
 * PRD-27 D-7 it is evidence rather than the whole record: the four `ack*` fields beside it name
 * the catalogue sentence and the values it interpolated, so the same acknowledgement can be read
 * back in either language.
 */
export const UpdateAccessRequestSchema = z
  .object({
    access: AudienceInputSchema,
    ackText: z.string().min(1).max(ACK_TEXT_MAX),
    ...ackRecordFields,
    /** The version of the access the maintainer read; a stale one is a conflict. */
    ifVersion: z.number().int().nonnegative().optional()
  })
  .strict()

export const MoveKnowledgeBaseRequestSchema = z.object({ to: OwnerRefSchema }).strict()

/** Strict like every other body (GRP-110): the base is the route's, never a list in the body. */
export const KnowledgeBaseSearchRequestSchema = SearchRequestSchema.omit({ scopeIds: true }).strict()

/** The base's own sharing and lending, as its "Who can use it" view reads them (maintainers). */
export const KnowledgeBaseAccessViewSchema = z.object({
  baseId: z.uuid(),
  access: AudienceViewSchema,
  /** Increments with every access change; sent back as `ifVersion`. */
  version: z.number().int().nonnegative(),
  /** Who shared each group, and whether an administrator did so by role (D-18). */
  sharedBy: z.array(
    z.object({
      group: GroupRefSchema,
      sharedByName: z.string().nullable(),
      sharedAt: z.iso.datetime(),
      approvedByName: z.string().nullable(),
      byAdministrator: z.boolean()
    })
  ),
  pendingRequest: KnowledgeBaseSchema.shape.pendingRequest,
  reach: z.number().int().nonnegative(),
  owner: OwnerViewSchema,
  /** For the picker: the groups the viewer manages (which never need review). */
  managedGroups: z.array(GroupWithSizeSchema)
})
export type KnowledgeBaseAccessView = z.infer<typeof KnowledgeBaseAccessViewSchema>

// ---------------------------------------------------------------------------
// Coverage: will everyone an agent is shared with get answers from its knowledge?
// ---------------------------------------------------------------------------

export const CoverageRequestSchema = z.object({ audience: AudienceInputSchema }).strict()

/**
 * For each base attached to an agent, measured against the audience being CHOSEN (GRP-045) —
 * not the one last published, which is the defect the previous report had (F-9).
 */
export const CoverageReportSchema = z.object({
  agentId: z.string(),
  audience: AudienceViewSchema,
  reach: z.number().int().nonnegative(),
  bases: z.array(
    z.object({
      baseId: z.uuid(),
      name: z.string(),
      owner: OwnerViewSchema,
      maintainedByCaller: z.boolean(),
      /**
       * - `covered` — everyone the agent reaches can use it;
       * - `lent` — it answers through this agent for everyone the agent reaches;
       * - `partial` — some of the audience will not get answers from it;
       * - `none` — nobody in the audience beyond its maintainers will.
       */
      status: z.enum(['covered', 'lent', 'partial', 'none']),
      missing: z.object({
        everyone: z.boolean(),
        groups: z.array(GroupWithSizeSchema),
        people: z.number().int().nonnegative()
      }),
      request: z
        .object({
          id: z.uuid(),
          status: z.enum(['pending', 'approved', 'declined', 'withdrawn', 'closed']),
          at: z.iso.datetime(),
          decidedByName: z.string().nullable(),
          note: z.string()
        })
        .nullable(),
      /**
       * Who let it answer through this agent, when it does (`lent`) — whether through a request or
       * directly from the base's "Agents that use it". Null when it is not lent, and `name` null
       * when that person's account is gone.
       */
      lentBy: z.object({ name: z.string().nullable(), at: z.iso.datetime(), byAdministrator: z.boolean() }).nullable(),
      /** Nobody but administrators can decide for it (D-17). */
      noMaintainer: z.boolean()
    })
  )
})
export type CoverageReport = z.infer<typeof CoverageReportSchema>

/**
 * What one agent's knowledge actually did for it, for the people who maintain the agent (AGM-120).
 */
export const AgentKnowledgeUsageSchema = z.object({
  agentId: z.string(),
  windowDays: z.number().int().positive(),
  searches: z.number().int().nonnegative(),
  zeroHitSearches: z.number().int().nonnegative(),
  answers: z.number().int().nonnegative(),
  groundedAnswers: z.number().int().nonnegative(),
  bases: z.array(
    z.object({
      baseId: z.uuid(),
      name: z.string(),
      ownerName: z.string(),
      mine: z.boolean(),
      documentCount: z.number().int().nonnegative(),
      storageBytes: z.number().int().nonnegative(),
      answersUsing: z.number().int().nonnegative(),
      lastUsedAt: z.iso.datetime().nullable()
    })
  )
})
export type AgentKnowledgeUsage = z.infer<typeof AgentKnowledgeUsageSchema>
